import { isEqual } from "lodash";

function protocolError(message) {
  return Object.assign(new Error(message), { kind: "protocol" });
}

/** Whether a remote row is a group header; a blank label still makes one. */
export function isHeader(item) {
  return typeof item?.header === "string";
}

function isGroup(item) {
  return Array.isArray(item?.children);
}

/** Returns the page unchanged, or throws a `protocol` error unless it is `{ items: Array, hasMore: Boolean }`. */
export function validateRemotePage(page) {
  if (!page || !Array.isArray(page.items) || typeof page.hasMore !== "boolean") {
    throw protocolError("Invalid remote page");
  }

  return page;
}

/** Merges root pages in server order, with global leaf identity and path-scoped groups. */
export function mergeRemotePage(
  previous,
  incoming,
  { hasMore = false, itemValue = "value", itemTitle = "title", selectableBranches = false } = {},
) {
  const leaves = new Map();
  let added = 0;

  function identity(item) {
    return typeof item === "object" && item !== null ? item[itemValue] : item;
  }

  function indexLeaves(items) {
    for (const item of items) {
      if (isGroup(item)) {
        indexLeaves(item.children);
      } else if (!isHeader(item)) {
        leaves.set(String(identity(item)), item);
      }
    }
  }

  function sections(items) {
    const list = [{ header: null, rows: [] }];

    for (const item of items) {
      if (isHeader(item)) {
        list.push({ header: item, rows: [] });
      } else {
        list.at(-1).rows.push(item);
      }
    }

    return list;
  }

  function mergeRows(out, right) {
    for (const item of right) {
      if (item == null || (typeof item === "object" && !Array.isArray(item.children) && identity(item) == null)) {
        throw protocolError("Missing remote identity");
      }

      if (isGroup(item)) {
        const key = String(identity(item) ?? item[itemTitle]);
        const existing = out.find((row) => isGroup(row) && String(identity(row) ?? row[itemTitle]) === key);
        const children = merge(existing?.children ?? [], item.children);

        const selectableParent = selectableBranches && item.selectable !== false;

        if (existing) {
          existing.children = children;
        } else if (children.length || selectableParent) {
          out.push({ ...item, children });

          if (selectableParent) {
            added++;
          }
        }
      } else {
        const key = String(identity(item));

        if (leaves.has(key)) {
          if (!isEqual(leaves.get(key), item)) {
            throw protocolError("Conflicting remote identity");
          }
        } else {
          leaves.set(key, item);
          added++;
          out.push(item);
        }
      }
    }
  }

  // A header row opens a section that runs to the next header; an incoming section joins the
  // section with the same header, and leading rows without a header join the last section.
  function merge(left, right) {
    const out = sections(left.map((item) => (isGroup(item) ? { ...item, children: [...item.children] } : item)));
    const [lead, ...headed] = sections(right);

    mergeRows(out.at(-1).rows, lead.rows);

    for (const section of headed) {
      let target = out.find((existing) => existing.header?.header === section.header.header);

      if (!target) {
        target = { header: section.header, rows: [] };
        out.push(target);
      }

      mergeRows(target.rows, section.rows);
    }

    return out.flatMap(({ header, rows }) => (header ? [header, ...rows] : rows));
  }

  indexLeaves(previous);
  const items = merge(previous, incoming);

  if (hasMore && added === 0) {
    throw protocolError("Remote page made no progress");
  }

  return items;
}

/**
 * Flattens groups into a header row followed by their leaves. Under `selectableBranches` a parent
 * not marked `selectable: false` stays a row above its children.
 */
export function remoteSearchRows(items, itemTitle = "title", selectableBranches = false) {
  return items.flatMap((item) => {
    if (isGroup(item)) {
      const children = remoteSearchRows(item.children, itemTitle, selectableBranches);

      if (selectableBranches && item.selectable !== false) {
        return [leafRow(item), ...children];
      }

      return [{ header: item[itemTitle] }, ...children];
    }

    return [leafRow(item)];
  });
}

function leafRow(item) {
  const row = typeof item === "object" && item !== null ? { ...item } : item;

  if (row && typeof row === "object") {
    delete row.hasChildren;
    delete row.children;
  }

  return row;
}
